import React, {
  useMemo,
  useCallback,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner, Spacer } from '@tw/tw-component-library';
import { Heading, Headers } from '@tw/tw-component-library/common-wt';
import { useFormConfiguration } from '../../container/forms/hooks/core/useFormConfiguration';
import { useFormContext } from '../../../contexts/useFormContext';
import { SectionKey, sectionKeyToDisplay } from '../../../types';
import { getSectionFlow } from '../../container/forms/hooks/useFormState';
import { FormState } from '../../container/forms/types/core/form-state.types';
import { FormProgress } from '../../container/formProgress/FormProgress';
import { useFocusFirstInput } from '../../../hooks/useFocusFirstInput';
import { StyledComponents } from './FormStack.styles';
import { FormCompletionModal } from './FormCompletionModal.tsx';
import { SectionCompletionModal } from './SectionCompletionModal.tsx';
import { createLogger } from '../../../utils/logger';

const logger = createLogger('FormWrapper');

const { Wrapper } = StyledComponents;

// Constants
const INITIAL_FORM_DATA = {
  payments_count: '',
  gridData: [],
  payment_spreadsheet: '',
  location: 'nonUk', // Default location for all workflows
};

interface FormCompletionState {
  isComplete: boolean;
  completedDate?: string;
  showCompletionModal: boolean;
  showSectionCompletionModal: boolean;
  completedSection?: SectionKey;
}

interface FormWrapperProps {
  children: React.ReactNode;
  showProgress?: boolean;
  showHeader?: boolean;
  isSingleSection?: boolean;
  sectionKey?: SectionKey;
  onFormComplete?: () => void;
}

export interface FormWrapperRef {
  handleFormCompletion: () => Promise<void>;
  showSectionCompletionModal: (section: SectionKey) => void;
}

// Extracted components
const FormHeader = React.memo(
  ({
    name,
    createdByName,
    currentSection,
    isLastSection,
    isSingleSection,
  }: {
    name?: string;
    createdByName?: string;
    currentSection: SectionKey;
    isLastSection: boolean;
    isSingleSection?: boolean;
  }) => (
    <>
      <Headers
        headerType="h1"
        subheading={<Headers showBar={false} headerType="h5" children={`Created by: ${createdByName}`} />}
      >
        {name}
      </Headers>
      <Spacer showLine lineStyle="dashed" size="2rem" />
      <Heading level={2}>
        {sectionKeyToDisplay[currentSection] ?? currentSection}
        {isLastSection && !isSingleSection && ' (Final Section)'}
      </Heading>
      <Spacer />
    </>
  ),
);

export const FormWrapper = forwardRef<FormWrapperRef, FormWrapperProps>(
  (
    {
      children,
      showProgress = true,
      showHeader = true,
      isSingleSection = false,
      sectionKey = 'payment_details',
      onFormComplete,
    },
    ref
  ) => {
    const navigate = useNavigate();
    const { formConfig, currentSection } = useFormConfiguration();
    const { formState, updateFormData, getCurrentSectionFlow } =
      useFormContext();
    const [isLoading, setIsLoading] = useState(true);

    // Get current section flow based on form data
    const currentSectionFlow = useMemo(() => {
      return getCurrentSectionFlow
        ? getCurrentSectionFlow()
        : getSectionFlow(formState.data);
    }, [getCurrentSectionFlow, formState.data]);
    const [shouldFocusInput] = useState(false);
    const [completionState, setCompletionState] = useState<FormCompletionState>(
      {
        isComplete: false,
        showCompletionModal: false,
        showSectionCompletionModal: false,
      }
    );
    const isInitializedRef = useRef(false);

    // Use the provided sectionKey for single-section forms, otherwise use currentSection
    const effectiveSectionKey = isSingleSection ? sectionKey : currentSection;

    useFocusFirstInput(effectiveSectionKey, shouldFocusInput);

    // Set loading state based on form config availability
    useEffect(() => {
      if (formConfig) {
        setIsLoading(false);
      }
    }, [formConfig]);

    // Initialize form data on mount
    useEffect(() => {
      if (isInitializedRef.current) return;

      try {
        const initialData = {
          ...INITIAL_FORM_DATA,
          ...formState.data,
          gridData:
            formState.data?.payments_count === '1-5Payments'
              ? formState.data?.gridData
              : undefined,
          payment_spreadsheet:
            formState.data?.payments_count === '5+Payments'
              ? formState.data?.payment_spreadsheet
              : undefined,
        };

        updateFormData({ data: initialData });
        isInitializedRef.current = true;
      } catch (error) {
        logger.error('Error initializing form data', { error: String(error) });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isLastSection = useCallback(() => {
      if (isSingleSection) return true;
      return (
        currentSectionFlow.indexOf(currentSection) ===
        currentSectionFlow.length - 1
      );
    }, [currentSection, isSingleSection, currentSectionFlow]);

    const handleFormCompletion = useCallback(async () => {
      try {
        const completedDate = new Date().toISOString();

        setCompletionState((prev) => ({
          ...prev,
          isComplete: true,
          completedDate,
          showCompletionModal: true,
          showSectionCompletionModal: false,
        }));

        updateFormData((prev: FormState) => ({
          ...prev,
          isComplete: true,
          completedDate,
          isSubmitting: false,
          isDirty: false,
        }));

        // Call the optional callback
        if (onFormComplete) {
          onFormComplete();
        }

        logger.state('Form completed successfully');
      } catch (error) {
        logger.error('Error in form completion', { error: String(error) });
      }
    }, [updateFormData, onFormComplete]);

    const showSectionCompletionModal = useCallback((section: SectionKey) => {
      const completedDate = new Date().toISOString();

      setCompletionState((prev) => ({
        ...prev,
        isComplete: false,
        completedDate,
        showCompletionModal: false,
        showSectionCompletionModal: true,
        completedSection: section,
      }));
    }, []);

    // Expose handleFormCompletion and showSectionCompletionModal through ref
    useImperativeHandle(
      ref,
      () => ({
        handleFormCompletion,
        showSectionCompletionModal,
      }),
      [handleFormCompletion, showSectionCompletionModal]
    );

    // Handle modal close
    // const handleCloseModal = useCallback(() => {
    //   setCompletionState((prev) => ({
    //     ...prev,
    //     showCompletionModal: false,
    //     showSectionCompletionModal: false,
    //   }));
    //   if (formState.isRejected) {
    //     navigate('/');
    //   }
    // }, [formState.isRejected, navigate]);

    // Handle start new form
    const handleStartNewForm = useCallback(() => {
      navigate('/');
    }, [navigate]);

    // Handle go to dashboard
    const handleGoToDashboard = useCallback(() => {
      navigate('/');
    }, [navigate]);

    // Memoize form data for header component
    const headerProps = useMemo(
      () => ({
        name: formState.data?.name,
        createdByName: formState.data?.createdByName,
        currentSection: effectiveSectionKey,
        isLastSection: isLastSection(),
        isSingleSection,
      }),
      [
        formState.data?.name,
        formState.data?.createdByName,
        effectiveSectionKey,
        isLastSection,
        isSingleSection,
      ]
    );

    // Check if form is rejected
    const isRejected = useMemo(
      () => formState.isRejected,
      [formState.isRejected]
    );

    // Memoize completion modal props
    const completionModalProps = useMemo(
      () => ({
        isOpen: completionState.showCompletionModal,
        completedDate: completionState.completedDate,
        onGoToDashboard: handleGoToDashboard,
        onStartNew: handleStartNewForm,
        isSingleSection,
        isRejected,
        sectionFlow: currentSectionFlow,
      }),
      [
        completionState.showCompletionModal,
        completionState.completedDate,
        handleGoToDashboard,
        handleStartNewForm,
        isSingleSection,
        isRejected,
        currentSectionFlow,
      ]
    );

    // Memoize section completion modal props
    const sectionCompletionModalProps = useMemo(
      () => ({
        isOpen: completionState.showSectionCompletionModal,
        completedDate: completionState.completedDate,
        onGoToDashboard: handleGoToDashboard,
        currentSection: completionState.completedSection || effectiveSectionKey,
        sectionProgress: formState.sectionProgress,
        isRejected,
        submittedSections: formState.submittedSections,
        sectionFlow: currentSectionFlow,
        formData: formState.data,
        workflowType: formState.workflowType,
      }),
      [
        completionState.showSectionCompletionModal,
        completionState.completedDate,
        completionState.completedSection,
        formState.submittedSections,
        handleGoToDashboard,
        effectiveSectionKey,
        formState.sectionProgress,
        isRejected,
        currentSectionFlow,
        formState.data,
        formState.workflowType,
      ]
    );

    // Render
    return (
      <>
        {showProgress && !isSingleSection && <FormProgress />}
        {isLoading ? (
          <Wrapper>
            <LoadingSpinner variant="fullPage" />
          </Wrapper>
        ) : (
          <Wrapper>
            {showHeader && <FormHeader {...headerProps} />}
            {children}
          </Wrapper>
        )}
        <Spacer />
        {completionState.showCompletionModal && (
          <FormCompletionModal {...completionModalProps} />
        )}
        {completionState.showSectionCompletionModal && (
          <SectionCompletionModal {...sectionCompletionModalProps} />
        )}
      </>
    );
  }
);
